import { ChevronDown, Sparkles } from "lucide-react";
import { useRefresh, useTranslate, useUpdate } from "ra-core";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import type { ContactNote, NoteIntent } from "../types";

const INTENT_STYLES: Record<NoteIntent, string> = {
  interested:
    "bg-green-100 text-green-900 dark:bg-green-900/40 dark:text-green-100",
  question: "bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-100",
  not_now:
    "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100",
  not_interested:
    "bg-zinc-200 text-zinc-900 dark:bg-zinc-700 dark:text-zinc-100",
  out_of_office:
    "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
  unsubscribe: "bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-100",
};
const INTENTS = Object.keys(INTENT_STYLES) as NoteIntent[];

/** The reply's intent label; sales change it from the menu. */
export const IntentChip = ({
  note,
  showEmpty,
}: {
  note: ContactNote;
  showEmpty?: boolean;
}) => {
  const translate = useTranslate();
  const refresh = useRefresh();
  const [update, { isPending }] = useUpdate();
  if (!note.intent && !showEmpty) return null;

  // a changed label no longer matches the AI's quote; the database records who set it and,
  // for interested or unsubscribe, updates the contact, hence the refresh
  const setIntent = (intent: NoteIntent | null) =>
    update(
      "contact_notes",
      {
        id: note.id,
        data: { intent, intent_quote: null },
        previousData: note,
      },
      { onSuccess: refresh },
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={isPending}>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium cursor-pointer",
            note.intent
              ? INTENT_STYLES[note.intent]
              : "border border-dashed text-muted-foreground",
          )}
          title={
            note.intent_by === "ai"
              ? translate("resources.notes.intent.by_ai")
              : undefined
          }
        >
          {note.intent_by === "ai" && <Sparkles className="size-3" />}
          {translate(
            note.intent
              ? `resources.notes.intent.values.${note.intent}`
              : "resources.notes.intent.set",
          )}
          <ChevronDown className="size-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {INTENTS.map((intent) => (
          <DropdownMenuItem key={intent} onSelect={() => setIntent(intent)}>
            {translate(`resources.notes.intent.values.${intent}`)}
          </DropdownMenuItem>
        ))}
        {note.intent && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setIntent(null)}>
              {translate("resources.notes.intent.clear")}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
